import { Col, Form } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import i18n from "i18next";
import { type FC, useMemo } from "react";

import type { VirtualSiteBatchUpdateEntity } from "src/app/features/VirtualSiteGroup/types/VirtualSiteBatchUpdateEntity";
import { DrawerForm } from "../../../../../components/DrawerForm/DrawerForm";
import { BatchUpdateFormRow } from "../../../../../components/DrawerForm/drawerFormStyles";
import { FieldLabel } from "../../../../../components/DrawerForm/FieldLabel";
import {
    blankToUndefined,
    optionalNonNegativeValidator,
} from "../../../../../components/Form/components/Validations/validationRules/NonNegative";
import { Checkbox, TextInput, TextTemplating } from "../../../../../components/Form/formComponents";
import { DatePickerInput } from "../../../../../components/Form/formComponents/DatePicker/DatePicker";
import { NumberInput } from "../../../../../components/Form/formComponents/Number/NumberInput";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import type { SearchFormProps } from "../../../../../components/Form/types/types";

type VirtualSiteBatchUpdateFormProps = SearchFormProps<VirtualSiteBatchUpdateEntity> & { form?: FormInstance };

// Field names as they appear in the Site form config from BE, where they differ from our VirtualSiteBatchUpdateEntity keys
// (see terminalParameters.* nesting used by SiteBatchUpdateForm for the same underlying fields).
const VIRTUAL_SITE_FORM_FIELD_NAME_OVERRIDES: Partial<Record<keyof VirtualSiteBatchUpdateEntity, string>> = {
    headerLines: "terminalParameters.headerLines",
    footerLines: "terminalParameters.footerLines",
    footerMandatStation: "terminalParameters.posFooter",
    tankLimit: "terminalParameters.tankLimit",
    eventLog: "terminalParameters.eventLog",
    nonstop: "terminalParameters.nonstop",
};

export const VirtualSiteBatchUpdateForm: FC<VirtualSiteBatchUpdateFormProps> = ({ searchCallback, form }) => {
    const { data: formDefinition } = useLoadFormDefinitionQuery("site");

    // Only expose fields that exist in the current Data Group's Site form config (varies by BP/IPASO/MADIC/APAC).
    const availableFieldNames = useMemo(
        () => new Set(formDefinition?.formConfiguration?.tabs.flatMap((tab) => tab.fields.map((field) => field.name)) ?? []),
        [formDefinition],
    );

    const isFieldAvailable = (fieldName: keyof VirtualSiteBatchUpdateEntity) =>
        availableFieldNames.has(VIRTUAL_SITE_FORM_FIELD_NAME_OVERRIDES[fieldName] ?? fieldName);

    return (
        <DrawerForm onSubmit={searchCallback} form={form} useSubmitButton={true} submitLabel={i18n.t("button.batchUpdate")}>
            <p>{i18n.t("batchUpdate.message")}</p>

            {isFieldAvailable("dealerName") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.dealerName")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="dealerName">
                                <TextInput name="dealerName" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateDealerName" valuePropName="checked">
                                <Checkbox name="updateDealerName" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("partnerId") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.partnerId")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="partnerId">
                                <TextInput name="partnerId" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updatePartnerId" valuePropName="checked">
                                <Checkbox name="updatePartnerId" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("siretNumber") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.siretNumber")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="siretNumber">
                                <TextInput name="siretNumber" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateSiretNumber" valuePropName="checked">
                                <Checkbox
                                    name="updateSiretNumber"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("validFrom") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.validFrom")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="validFrom">
                                <DatePickerInput name="validFrom" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateValidFrom" valuePropName="checked">
                                <Checkbox name="updateValidFrom" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("partnerCount") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.partnerCount")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="partnerCount"
                                rules={[{ validator: optionalNonNegativeValidator }]}
                                getValueFromEvent={blankToUndefined}
                            >
                                <NumberInput name="partnerCount" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updatePartnerCount" valuePropName="checked">
                                <Checkbox
                                    name="updatePartnerCount"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("validTo") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.validTo")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="validTo">
                                <DatePickerInput name="validTo" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateValidTo" valuePropName="checked">
                                <Checkbox name="updateValidTo" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("dealerStreet") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.dealerStreet")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="dealerStreet">
                                <TextInput name="dealerStreet" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateDealerStreet" valuePropName="checked">
                                <Checkbox
                                    name="updateDealerStreet"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("dealerHouseNumber") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.dealerHouseNumber")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="dealerHouseNumber">
                                <TextInput name="dealerHouseNumber" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateDealerHouseNumber" valuePropName="checked">
                                <Checkbox
                                    name="updateDealerHouseNumber"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("dealerCity") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.dealerCity")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="dealerCity">
                                <TextInput name="dealerCity" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateDealerCity" valuePropName="checked">
                                <Checkbox name="updateDealerCity" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("dealerPostalCode") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.dealerPostalCode")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="dealerPostalCode">
                                <TextInput name="dealerPostalCode" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateDealerPostalCode" valuePropName="checked">
                                <Checkbox
                                    name="updateDealerPostalCode"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("dealerCountry") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.dealerCountry")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="dealerCountry">
                                <TextInput name="dealerCountry" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateDealerCountry" valuePropName="checked">
                                <Checkbox
                                    name="updateDealerCountry"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("intraCommunityVat") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.intraCommunityVat")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="intraCommunityVat">
                                <TextInput name="intraCommunityVat" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateIntraCommunityVat" valuePropName="checked">
                                <Checkbox
                                    name="updateIntraCommunityVat"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("headerLines") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.headerLines")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="headerLines">
                                <TextTemplating name="headerLines" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateHeaderLines" valuePropName="checked">
                                <Checkbox
                                    name="updateHeaderLines"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("footerLines") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.footerLines")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="footerLines">
                                <TextTemplating name="footerLines" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateFooterLines" valuePropName="checked">
                                <Checkbox
                                    name="updateFooterLines"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("footerMandatStation") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.posFooter")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="footerMandatStation">
                                <TextTemplating name="footerMandatStation" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updatePosFooter" valuePropName="checked">
                                <Checkbox name="updatePosFooter" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("tankLimit") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.tankLimit")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="tankLimit"
                                rules={[{ validator: optionalNonNegativeValidator }]}
                                getValueFromEvent={blankToUndefined}
                            >
                                <NumberInput name="tankLimit" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateTankLimit" valuePropName="checked">
                                <Checkbox name="updateTankLimit" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("eventLog") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.eventLog")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="eventLog" valuePropName="checked">
                                <Checkbox name="eventLog" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateEventLog" valuePropName="checked">
                                <Checkbox name="updateEventLog" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("nonstop") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.nonstop")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="nonstop" valuePropName="checked">
                                <Checkbox name="nonstop" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateNonstop" valuePropName="checked">
                                <Checkbox name="updateNonstop" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("notificationEmail") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.notificationEmail")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="notificationEmail">
                                <TextInput name="notificationEmail" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateNotificationEmail" valuePropName="checked">
                                <Checkbox
                                    name="updateNotificationEmail"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("vwTerminalAvailableGuiProfile") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.guiProfile")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="vwTerminalAvailableGuiProfile">
                                <TextInput name="vwTerminalAvailableGuiProfile" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateVwTerminalAvailableGuiProfile" valuePropName="checked">
                                <Checkbox
                                    name="updateVwTerminalAvailableGuiProfile"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("guiWelcomeText") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.guiWelcomeText")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="guiWelcomeText">
                                <TextInput name="guiWelcomeText" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateGuiWelcomeText" valuePropName="checked">
                                <Checkbox
                                    name="updateGuiWelcomeText"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("acousticNotification") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.acousticNotification")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="acousticNotification" valuePropName="checked">
                                <Checkbox name="acousticNotification" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateAcousticNotification" valuePropName="checked">
                                <Checkbox
                                    name="updateAcousticNotification"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("acousticNotificationOfTransactionResult") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.acousticNotificationOfTransactionResult")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="acousticNotificationOfTransactionResult" valuePropName="checked">
                                <Checkbox name="acousticNotificationOfTransactionResult" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateAcousticNotificationOfTransactionResult" valuePropName="checked">
                                <Checkbox
                                    name="updateAcousticNotificationOfTransactionResult"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}
        </DrawerForm>
    );
};
